import {
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';

import { UntypedFormControl } from '@angular/forms';
import { fromEvent, Subject } from 'rxjs';

import { filter, switchMapTo, take, takeUntil } from 'rxjs/operators';

import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { EditModeDirective } from './edit-mode.directive';
import { ViewModeDirective } from './view-mode.directive';

@EnableClassTelemetry()
@Component({
  selector: 'app-inline-edit',
  templateUrl: './inline-edit.component.html',
  styleUrls: ['./inline-edit.component.scss'],
  standalone: false,
})
export class InlineEditComponent implements OnInit, OnChanges, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() control: UntypedFormControl;
  @Input() updateOn: 'blur' | 'input' = 'input';
  @Input() mode: 'view' | 'edit' = 'view';

  @Output() update = new EventEmitter();
  @Output() modeChange = new EventEmitter<'view' | 'edit'>();

  @ContentChild(ViewModeDirective, /* TODO: add static flag */ {}) viewModeTpl: ViewModeDirective;
  @ContentChild(EditModeDirective, /* TODO: add static flag */ {}) editModeTpl: EditModeDirective;

  @HostBinding('class.inline-edit--view') get viewModeClass() {
    return this.mode === 'view';
  }

  @HostBinding('class.inline-edit--edit') get editModeClass() {
    return this.mode === 'edit';
  }

  @HostBinding('class.inline-edit--disabled') get isDisabled() {
    return this.control && this.control.disabled;
  }

  editMode = new Subject<void>();
  editMode$ = this.editMode.asObservable();

  originalValue: any;

  unsub = new Subject<void>();

  constructor(private host: ElementRef) {}

  get currentView() {
    return this.mode === 'view' ? this.viewModeTpl.tpl : this.editModeTpl.tpl;
  }

  ngOnInit() {
    this.viewModeHandler();
    this.editModeHandler();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.mode && changes.mode.currentValue && changes.mode.currentValue !== this.mode) {
      if (changes.mode.currentValue === 'edit') {
        this.toEditMode();
      } else {
        this.toViewMode();
      }
    }
  }

  ngOnDestroy() {
    this.unsub.next();
  }

  toViewMode(reset?, event?) {
    if (this.control && this.control.invalid) {
      if (event) {
        event.preventDefault();
      }

      if (this.mode === 'view') {
        // this can happen when external mode change
        this.mode = 'edit';
        this.modeChange.emit(this.mode);
      }
    } else if (reset && this.control) {
      this.control.setValue(this.originalValue, { emitEvent: this.updateOn === 'input' });
      this.mode = 'view';
      this.modeChange.emit(this.mode);
    } else if (!this.control || !this.control.invalid) {
      this.mode = 'view';
      this.modeChange.emit(this.mode);
      this.update.emit();
    }
  }

  toEditMode() {
    if (this.editModeTpl && (!this.control || !this.control.disabled)) {
      if (this.control) {
        this.originalValue = this.control.value;
      }

      this.mode = 'edit';
      this.modeChange.emit(this.mode);
      this.editMode.next();

      setTimeout(() => (this.originalValue = this.inputValue));
    }
  }

  private get inputValue() {
    if (this.control) {
      return this.control.value;
    }

    const input = this.findInputElement();

    return input.value;
  }

  private set inputValue(value) {
    if (this.control) {
      this.control.setValue(value);
    } else {
      const input = this.findInputElement();

      input.value = value;
    }
  }

  public get getMode() {
    return this.mode;
  }

  private findInputElement() {
    let input = { value: undefined };
    input = input || this.element.getElementsByTagName('input').item(0);
    if (!input) {
      input = this.element.getElementsByTagName('textarea').item(0);
      this.modeChange.emit(this.mode);
    }
    return input;
  }

  private get element() {
    return this.host.nativeElement;
  }

  private viewModeHandler() {
    fromEvent(this.element, 'click')
      .pipe(takeUntil(this.unsub))
      .subscribe(() => this.toEditMode());
  }

  private editModeHandler() {
    const bodyElement = document.querySelector('body');
    const clickOutside$ = fromEvent(bodyElement, 'click').pipe(
      filter(
        ($event) => ($event.target as any).isConnected && !this.element.contains($event.target)
      ),
      take(1)
    );

    this.editMode$
      .pipe(
        switchMapTo(clickOutside$),
        takeUntil(this.unsub),
        filter(() => this.mode === 'edit')
      )
      .subscribe((event) => this.toViewMode(false, event));
  }
}
